import { useState } from 'react';
import { X, UserPlus, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth, API } from '../App';
import { toast } from 'sonner';
import axios from 'axios';

const JoinAsCoHostModal = ({ isOpen, onClose }) => {
  const { token } = useAuth();
  const [eventCode, setEventCode] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!eventCode.trim()) {
      toast.error('Please enter an event code');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/events/join-as-cohost`,
        { event_code: eventCode.trim().toUpperCase() },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      toast.success(response.data.message || 'Co-host request submitted successfully!');
      setEventCode('');
      onClose();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to submit co-host request';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setEventCode('');
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-orange-500 to-orange-600 rounded-t-lg">
          <div className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-white" />
            <h2 className="text-lg font-bold text-white">Join as Co-host</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-4">
              Enter the event code to request co-host access. The event host will receive your request for approval.
            </p>
            
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Event Code *
            </label>
            <Input
              type="text"
              value={eventCode}
              onChange={(e) => setEventCode(e.target.value.toUpperCase())}
              placeholder="Enter event code (e.g., ABC123_456789)"
              className="w-full"
              disabled={loading}
              maxLength={20}
            />
            <p className="text-xs text-gray-500 mt-1">
              You can find the event code from the event creator
            </p>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <p className="text-xs text-blue-800">
              <strong>Note:</strong> After submitting your request, you'll need to wait for the event host to approve before you can access co-host features.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Submit Request
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JoinAsCoHostModal;
